import React, { useEffect, useState, useCallback } from 'react';
import { industries } from '../../../constants/options';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, Typography, FormControl,
  InputLabel, Select, MenuItem, CircularProgress,
  Grid, Divider, InputAdornment, Paper, Card, 
  CardContent, Radio,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import ArchiveIcon from '@mui/icons-material/Archive';
import { 
  Close as CloseIcon,
  Title as TitleIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
  CalendarToday,
  Person as PersonIcon,
  Phone,
  Email,
  LocationOn,
  CheckCircle,
  Cancel,
  Business as BusinessIcon,
  Handyman as ServiceIcon,
  ShoppingBag as ProductIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';

// Configuración global de SweetAlert2 con z-index más alto
Swal.mixin({
  customClass: {
    container: 'swal-overlay-custom',
    popup: 'swal-popup-custom'
  },
  backdrop: 'rgba(0,0,0,0.4)',
  zIndex: 9999
});

// Estilos CSS globales para asegurar que SweetAlert2 se muestre correctamente
const injectGlobalStyles = () => {
  const style = document.createElement('style');
  style.textContent = `
    .swal-overlay-custom {
      z-index: 9999 !important;
    }
    .swal-popup-custom {
      z-index: 10000 !important;
    }
    .swal2-container {
      z-index: 9999 !important;
    }
  `;
  document.head.appendChild(style);
};

injectGlobalStyles();

const ServiceDialog = ({ 
  open,
  currentService,
  serviceForm,
  loading,
  onClose,
  onSave,
  onInputChange,
  showServiceType
}) => {
  const [hasChanges, setHasChanges] = useState(false);
  const [tempServiceType, setTempServiceType] = useState(serviceForm.type_publish || 'Servicio');
  const [tempServiceSubType, setTempServiceSubType] = useState(serviceForm.type_service || 'Oferta');

  useEffect(() => {
    if (open) {
      if (serviceForm.type_publish !== 'Servicio') {
        setTempServiceSubType(null);
      } else {
        setTempServiceSubType(serviceForm.type_service || 'Oferta');
      }
      setTempServiceType(serviceForm.type_publish || 'Servicio');
    }
  }, [open, serviceForm.type_publish, serviceForm.type_service]);

  const handleTypeChange = (type) => {
    setTempServiceType(type);
    
    if (type === 'Bien') {
      setTempServiceSubType(null);
      onInputChange({ 
        target: { 
          name: 'type_service', 
          value: null 
        } 
      });
    }
    
    onInputChange({ 
      target: { 
        name: 'type_publish', 
        value: type 
      } 
    });
  };

  const handleSubTypeChange = (subType) => {
    setTempServiceSubType(subType);
    onInputChange({ target: { name: 'type_service', value: subType } });
  };
  
  useEffect(() => {
    if (open) {
      const initialData = { 
        ...serviceForm,
        type_publish: serviceForm.type_publish || 'Servicio',
        type_service: serviceForm.type_service || 'Oferta',
        // Establecer fecha actual si no es una edición
        date_publish: currentService ? serviceForm.date_publish : new Date().toISOString()
      };
      
      setTempServiceType(initialData.type_publish);
      setTempServiceSubType(initialData.type_service);
    }
  }, [open, serviceForm, currentService]);

  useEffect(() => {
    if (open && currentService) {
      const changedFields = Object.keys(serviceForm).filter(key => {
        if (key === 'image' || key === 'visits' || key === 'date_publish') return false;
        const originalValue = currentService[key];
        const currentValue = serviceForm[key];
        return JSON.stringify(originalValue) !== JSON.stringify(currentValue);
      });

      setHasChanges(changedFields.length > 0);
    } else if (open && !currentService) {
      const requiredFields = ['title', 'description', 'category'];
      const hasRequiredData = requiredFields.every(field => 
        serviceForm[field] && serviceForm[field].trim() !== ''
      );
      setHasChanges(hasRequiredData);
    }
  }, [open, currentService, serviceForm]);

  const handleSaveWithAlert = useCallback(async () => {
    try {
      // Usar la fecha actual para nuevos servicios
      const formattedDate = currentService 
        ? serviceForm.date_publish 
        : new Date().toISOString();

      const finalData = {
        ...serviceForm,
        type_publish: tempServiceType,
        type_service: tempServiceType === 'Servicio' ? tempServiceSubType : null,
        date_publish: formattedDate
      };

      await onSave(finalData);
      
      await Swal.fire({
        title: currentService ? 'Publicación actualizada' : 'Publicación creada',
        text: currentService 
          ? 'Los cambios se han guardado correctamente' 
          : 'Tu publicación ha sido creada con éxito',
        icon: 'success',
        confirmButtonColor: '#0488AF',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false
      });
      
      onClose();
    } catch (error) {
      console.error("Error al guardar:", error);
      await Swal.fire({
        title: 'Error',
        text: error.message || 'Ocurrió un error al guardar la publicación',
        icon: 'error',
        confirmButtonColor: '#C42251'
      });
    }
  }, [currentService, onClose, onSave, serviceForm, tempServiceType, tempServiceSubType]);

  const handleCloseWithConfirm = useCallback(async () => {
    if (!hasChanges) {
      onClose();
      return;
    }

    const result = await Swal.fire({
      title: '¿Descartar cambios?',
      text: 'Tienes modificaciones sin guardar',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#0488AF',
      cancelButtonColor: '#C42251',
      confirmButtonText: 'Sí, descartar',
      cancelButtonText: 'Cancelar',
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      focusConfirm: false,
      focusCancel: true
    });

    if (result.isConfirmed) {
      onClose();
    }
  }, [hasChanges, onClose]);
  const validateEmail = (email) => {
  const re = /^[^@]+@[^@]+\.[^@]+$/;
  return re.test(String(email).toLowerCase());
};

 const isFormValid = useCallback(() => {
  const requiredFields = [
    'title', 'description', 'category',
    'contact', 'email', 'location', 'name_contact'
  ];
  
  const hasRequiredFields = requiredFields.every(field => {
    const value = serviceForm[field];
    return value !== undefined && value !== null && value !== '';
  });
  
  const hasValidTypes = tempServiceType && 
                       (tempServiceType !== 'Servicio' || tempServiceSubType);
  
  const isValidEmail = validateEmail(serviceForm.email);
  
  return hasRequiredFields && hasValidTypes && isValidEmail;
}, [serviceForm, tempServiceType, tempServiceSubType]);

  const renderPublicationTypeSection = () => (
    <Grid item xs={12}>
      <Typography variant="subtitle1" sx={{
        mb: 2, color: '#09274A', fontWeight: 600,
        display: 'flex', alignItems: 'center',
        '& svg': { mr: 1.5, color: '#0488AF' }
      }}>
        <BusinessIcon />
        Tipo de Publicación
      </Typography>

      <Card sx={{ p: 2, borderRadius: '12px', border: '1px solid #e0e0e0' }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box
                onClick={() => handleTypeChange('Servicio')}
              sx={{
                textAlign: 'center',
                cursor: 'pointer',
                border: `2px solid ${tempServiceType === 'Servicio' ? '#0488AF' : '#e0e0e0'}`,
                borderRadius: '12px',
                py: 2,
                transition: 'all 0.3s',
                '&:hover': {
                  borderColor: tempServiceType === 'Servicio' ? '#0488AF' : '#bdbdbd',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                }
              }}
            >
              <ServiceIcon sx={{ fontSize: '2rem', color: tempServiceType === 'Servicio' ? '#0488AF' : '#757575', mb: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Servicio
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Profesional o laboral
              </Typography>
              <Radio
                checked={tempServiceType === 'Servicio'}
                value="Servicio"
                name="type-radio"
                size="small"
                sx={{ mt: 1 }}
              />
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Box
                onClick={() => handleTypeChange('Bien/Producto')}
              sx={{
                textAlign: 'center',
                cursor: 'pointer',
                border: `2px solid ${tempServiceType === 'Bien/Producto' ? '#1BBB55' : '#e0e0e0'}`,
                borderRadius: '12px',
                py: 2,
                transition: 'all 0.3s',
                '&:hover': {
                  borderColor: tempServiceType === 'Bien/Producto' ? '#1BBB55' : '#bdbdbd',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                }
              }}
            >
              <ProductIcon sx={{ fontSize: '2rem', color: tempServiceType === 'Bien/Producto' ? '#1BBB55' : '#757575', mb: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Bien/Producto
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Físico o material
              </Typography>
              <Radio
                checked={tempServiceType === 'Bien/Producto'}
                value="Bien/Producto"
                name="type-radio"
                size="small"
                sx={{ mt: 1 }}
              />
            </Box>
          </Grid>
        </Grid>

        {tempServiceType === 'Servicio' && (
          <Box mt={3}>
            <Typography variant="subtitle2" sx={{ mb: 2, color: '#09274A' }}>
              Selecciona el tipo de servicio:
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box
                    onClick={() => handleSubTypeChange('Oferta')}
                  sx={{
                    textAlign: 'center',
                    cursor: 'pointer',
                    border: `2px solid ${tempServiceSubType === 'Oferta' ? '#0488AF' : '#e0e0e0'}`,
                    borderRadius: '12px',
                    py: 2,
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: tempServiceSubType === 'Oferta' ? '#0488AF' : '#bdbdbd',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                    }
                  }}
                >
                  <UnarchiveIcon sx={{ fontSize: '2rem', color: tempServiceSubType === 'Oferta' ? '#0488AF' : '#757575', mb: 1 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Oferta
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Ofreces este servicio
                  </Typography>
                  <Radio
                    checked={tempServiceSubType === 'Oferta'}
                    value="Oferta"
                    name="subtype-radio"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box
                    onClick={() => handleSubTypeChange('Demanda')}
                  sx={{
                    textAlign: 'center',
                    cursor: 'pointer',
                    border: `2px solid ${tempServiceSubType === 'Demanda' ? '#FF9800' : '#e0e0e0'}`,
                    borderRadius: '12px',
                    py: 2,
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: tempServiceSubType === 'Demanda' ? '#FF9800' : '#bdbdbd',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                    }
                  }}
                >
                  <ArchiveIcon sx = {{ fontSize: '2rem', color: tempServiceSubType === 'Demanda' ? '#FF9800' : '#757575', mb: 1 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Demanda
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Solicitas este servicio
                  </Typography>
                  <Radio
                    checked={tempServiceSubType === 'Demanda'}
                    value="Demanda"
                    name="subtype-radio"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </Card>
    </Grid>
  );

 const renderTextField = ({ name, label, value, required, disabled, multiline, rows, type, startIcon, error, helperText }) => (
  <TextField
    fullWidth
    label={label}
    name={name}
    value={value}
    onChange={onInputChange}
    required={required}
    disabled={disabled || loading}
    multiline={multiline}
    rows={rows}
    type={type}
    error={error}
    helperText={helperText}
    InputLabelProps={type === 'date' ? { shrink: true } : undefined}
    InputProps={{
      startAdornment: startIcon && (
        <InputAdornment position="start">
          {startIcon}
        </InputAdornment>
      ),
    }}
    variant="outlined"
    sx={{
      '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        alignItems: multiline ? 'flex-start' : 'center',
        '& fieldset': {
          borderColor: '#e0e0e0'
        },
        '&:hover fieldset': {
          borderColor: '#0488AF'
        }
      },
      '& .MuiInputAdornment-root': {
        marginTop: multiline ? '8px' : 0
      }
    }}
  />
);

  return (
    <Dialog 
      open={open} 
      onClose={handleCloseWithConfirm}
      fullWidth 
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
        }
      }}
    >
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #09274A 0%, #0488AF 100%)',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 2.5,
        px: 4,
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: '5%',
          width: '90%',
          height: '1px',
          background: 'rgba(255, 255, 255, 0.2)'
        }
      }}>
        <Box display="flex" alignItems="center" gap={2}>
          <BusinessIcon sx={{ fontSize: '1.8rem' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.3rem' }}>
            {currentService ? 'Editar Publicación' : 'Nueva Publicación'}
          </Typography>
        </Box>
        <IconButton 
          onClick={handleCloseWithConfirm}
          sx={{ 
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ py: 4, px: 4, background: '#f9fafc' }}>
        <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', background: 'white' }}>
          <Grid container spacing={3}>
            {renderPublicationTypeSection()}
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ 
                mb: 2, 
                color: '#09274A',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                '& svg': {
                  mr: 1.5,
                  color: '#0488AF'
                }
              }}>
                <TitleIcon />
                Información Básica
              </Typography>
            </Grid>

            <Grid item xs={12}>
              {renderTextField({
                name: 'title',
                label: 'Título',
                value: serviceForm.title,
                required: true,
                startIcon: <TitleIcon color="action" />
              })}
            </Grid>

            <Grid item xs={12}>
              {renderTextField({
                name: 'description',
                label: 'Descripción detallada',
                value: serviceForm.description,
                required: true,
                multiline: true,
                rows: 4,
                startIcon: <DescriptionIcon color="action" />
              })}
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ 
                my: 1,
                '&::before, &::after': {
                  borderColor: 'rgba(0, 0, 0, 0.08)'
                }
              }} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ 
                mb: 2, 
                color: '#09274A',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                '& svg': {
                  mr: 1.5,
                  color: '#0488AF'
                }
              }}>
                <CategoryIcon />
                Categorización
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required disabled={loading}>
                <InputLabel sx={{ color: 'text.secondary' }}>Categoría</InputLabel>
                <Select
                  name="category"
                  value={serviceForm.category}
                  onChange={onInputChange}
                  label="Categoría"
                  sx={{
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e0e0e0'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0488AF'
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  {industries.map((industry) => (
                    <MenuItem key={industry.value} value={industry.value}>
                      {industry.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fecha de publicación"
                name="date_publish"
                value={serviceForm.date_publish ? new Date(serviceForm.date_publish).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}
                required
                disabled
                type="date"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday color="action" />
                    </InputAdornment>
                  ),
                  readOnly: true
                }}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '& fieldset': {
                      borderColor: '#e0e0e0'
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ 
                my: 1,
                '&::before, &::after': {
                  borderColor: 'rgba(0, 0, 0, 0.08)'
                }
              }} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ 
                mb: 2, 
                color: '#09274A',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                '& svg': {
                  mr: 1.5,
                  color: '#0488AF'
                }
              }}>
                <PersonIcon />
                Información de Contacto
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              {renderTextField({
                name: 'name_contact',
                label: 'Nombre de contacto',
                value: serviceForm.name_contact,
                required: true,
                startIcon: <PersonIcon color="action" />
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderTextField({
                name: 'contact',
                label: 'Teléfono de contacto',
                value: serviceForm.contact,
                required: true,
                startIcon: <Phone color="action" />
              })}
            </Grid>
          <Grid item xs={12} md={6}>
  {renderTextField({
    name: 'email',
    label: 'Email de contacto',
    value: serviceForm.email,
    required: true,
    type: 'email',
    startIcon: <Email color="action" />,
    error: !!(serviceForm.email && !validateEmail(serviceForm.email)), // Aseguramos un booleano
    helperText: serviceForm.email && !validateEmail(serviceForm.email)
      ? 'Ingrese un email válido con un dominio correcto'
      : '',
  })}
</Grid>
            <Grid item xs={12} md={6}>
              {renderTextField({
                name: 'location',
                label: 'Ubicación',
                value: serviceForm.location,
                required: true,
                startIcon: <LocationOn color="action" />
              })}
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ 
                my: 1,
                '&::before, &::after': {
                  borderColor: 'rgba(0, 0, 0, 0.08)'
                }
              }} />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required disabled={loading}>
                <InputLabel sx={{ color: 'text.secondary' }}>Estado</InputLabel>
                <Select
                  name="status"
                  value={serviceForm.status}
                  onChange={onInputChange}
                  label="Estado"
                  sx={{
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e0e0e0'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0488AF'
                    }
                  }}
                >
                  <MenuItem value="active">
                    <Box display="flex" alignItems="center">
                      <CheckCircle sx={{ color: '#2e7d32', mr: 1.5 }} />
                      <Box>
                        <Typography>Activo</Typography>
                        <Typography variant="caption" color="text.secondary">Visible para todos los usuarios</Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                  <MenuItem value="inactive">
                    <Box display="flex" alignItems="center">
                      <Cancel sx={{ color: '#c62828', mr: 1.5 }} />
                      <Box>
                        <Typography>Inactivo</Typography>
                        <Typography variant="caption" color="text.secondary">No visible públicamente</Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 3, 
        borderTop: '1px solid rgba(0, 0, 0, 0.08)',
        background: '#f9fafc',
        justifyContent: 'space-between'
      }}>
        <Button 
          onClick={handleCloseWithConfirm}
          sx={{ 
            color: '#666',
            px: 3,
            py: 1,
            borderRadius: '8px',
            border: '1px solid #ddd',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              borderColor: '#ccc'
            }
          }}
          disabled={loading}
        >
          Cancelar
        </Button>
        
        <Button 
          variant="contained"
          onClick={handleSaveWithAlert}
          disabled={!isFormValid() || loading || (currentService && !hasChanges)}
          sx={{ 
            background: 'linear-gradient(135deg, #09274A 0%, #0488AF 100%)',
            color: 'white',
            borderRadius: '8px',
            px: 4,
            py: 1,
            minWidth: '180px',
            boxShadow: '0 2px 4px rgba(9, 39, 74, 0.2)',
            '&:hover': { 
              background: 'linear-gradient(135deg, #071D3A 0%, #03779C 100%)',
              boxShadow: '0 4px 8px rgba(9, 39, 74, 0.3)'
            },
            '&:disabled': {
              background: '#e0e0e0',
              color: '#9e9e9e',
              boxShadow: 'none'
            }
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <Box display="flex" alignItems="center">
              <CheckCircle sx={{ mr: 1, fontSize: '1.2rem' }} />
              {currentService ? 'Actualizar' : 'Crear Publicación'}
            </Box>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceDialog;